import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@pps/ui/components/ui/button';
import { toast } from 'sonner';
import { useWhatsAppConversations } from '@/hooks/useWhatsApp';
import { playNotificationSound } from '@/lib/notificationSound';
import { Instagram } from 'lucide-react';
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';

function ChannelButton({
  channel,
  icon: Icon,
  route,
  iconClassName,
}: {
  channel: 'whatsapp' | 'instagram';
  icon: React.ComponentType<{ className?: string }>;
  route: string;
  iconClassName?: string;
}) {
  const navigate = useNavigate();
  const { data: conversations = [] } = useWhatsAppConversations();

  const unreads = conversations
    .filter(c => c.channel === channel)
    .reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return (
    <Button
      variant="ghost"
      size="icon"
      className="relative h-9 w-9"
      onClick={() => navigate(route)}
      title={channel === 'whatsapp' ? 'WhatsApp' : 'Instagram'}
    >
      <Icon className={`h-4 w-4 ${iconClassName || ''}`} />
      {unreads > 0 && (
        <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
          {unreads > 99 ? '99+' : unreads}
        </span>
      )}
    </Button>
  );
}

export function SocialNotifications() {
  const navigate = useNavigate();
  const { data: conversations = [] } = useWhatsAppConversations();

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);
  const prevUnread = useRef(totalUnread);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    if (totalUnread > prevUnread.current) {
      const newest = conversations.find(c => c.unread_count > 0);
      const contactLabel = newest?.contact_name || newest?.phone || 'desconhecido';
      const channelLabel = newest?.channel === 'instagram' ? 'Instagram' : 'WhatsApp';
      const route = newest?.channel === 'instagram' ? '/instagram' : '/whatsapp';

      toast(`Nova mensagem de ${contactLabel} (${channelLabel})`, {
        action: { label: 'Abrir', onClick: () => navigate(route) },
      });

      playNotificationSound();

      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          const n = new Notification(`Nova mensagem ${channelLabel}`, {
            body: contactLabel,
            icon: '/favicon.png',
            tag: 'social-new-msg',
          });
          n.onclick = () => {
            window.focus();
            navigate(route);
            n.close();
          };
        } catch {
          // Silent fail
        }
      }
    }
    prevUnread.current = totalUnread;
  }, [totalUnread, conversations, navigate]);

  return (
    <>
      <ChannelButton channel="whatsapp" icon={WhatsAppIcon} route="/whatsapp" />
      <ChannelButton channel="instagram" icon={Instagram} route="/instagram" />
    </>
  );
}
